import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  fetchLatestVideoCallManagement,
  createVideoCallManagement,
  updateVideoCallManagement,
  setVideoCallManagementFormDirty,
  updateLatestSettingsFromForm,
  clearLatestSettingsError
} from "../../redux/Slices/VideoCallManagementSlice";
import {
  VideoCallManagementVideoQualityEnum,
  VideoCallManagementServerRegionEnum,
  VideoCallManagementCallDurationEnum,
  VideoCallManagementEnum,
  VideoCallManagementFormState
} from "../../modules/shared/model";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { 
  Video, 
  BarChart2, 
  AlertTriangle, 
  Clock, 
  Settings,
  Save,
  Loader2
} from "lucide-react";

const VideoCallManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    latestSettings,
    latestSettingsLoading,
    latestSettingsError,
    isFormDirty,
    lastSavedAt
  } = useAppSelector((state) => state.videoCallManagement);

  const [formState, setFormState] = useState<VideoCallManagementFormState>({
    basicSettings: {
      enableVideoCall: true,
      enableHDQuality: true,
      enableCallRecording: false,
      enableBandwidthOptimization: true,
    },
    callLimits: {
      callDuration: VideoCallManagementCallDurationEnum["1 hour"],
    },
    qualitySettings: {
      videoQuality: VideoCallManagementVideoQualityEnum.Auto,
    },
    serverSettings: {
      serverRegion: VideoCallManagementServerRegionEnum.Auto,
    },
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchLatestVideoCallManagement());
  }, [dispatch]);

  useEffect(() => {
    if (latestSettings) {
      setFormState({
        basicSettings: {
          enableVideoCall: latestSettings.enableVideoCall ?? true,
          enableHDQuality: latestSettings.enableHDQuality ?? true,
          enableCallRecording: latestSettings.enableCallRecording ?? false,
          enableBandwidthOptimization: latestSettings.enableBandwidthOptimization ?? true,
        },
        callLimits: {
          callDuration: latestSettings.callDuration?.id ?? VideoCallManagementCallDurationEnum["1 hour"],
        },
        qualitySettings: {
          videoQuality: latestSettings.videoQuality?.id ?? VideoCallManagementVideoQualityEnum.Auto,
        },
        serverSettings: {
          serverRegion: latestSettings.serverRegions?.id ?? VideoCallManagementServerRegionEnum.Auto,
        },
      });
    }
  }, [latestSettings]);

  const handleToggle = (field: string, value: boolean) => {
    setFormState(prev => ({
      ...prev,
      basicSettings: {
        ...prev.basicSettings,
        [field]: value
      }
    }));
    dispatch(setVideoCallManagementFormDirty(true));
  };

  const handleSelectChange = (category: string, field: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof VideoCallManagementFormState],
        [field]: value
      }
    }));
    dispatch(setVideoCallManagementFormDirty(true));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const saveData = {
        enableVideoCall: formState.basicSettings.enableVideoCall,
        enableHDQuality: formState.basicSettings.enableHDQuality,
        enableCallRecording: formState.basicSettings.enableCallRecording,
        enableBandwidthOptimization: formState.basicSettings.enableBandwidthOptimization,
        callDuration: {
          id: Number(formState.callLimits.callDuration)
        },
        videoQuality: {
          id: Number(formState.qualitySettings.videoQuality)
        },
        serverRegions: {
          id: Number(formState.serverSettings.serverRegion)
        }
      };

      let result;

      if (!latestSettings) {
        // Create new configuration if none exists
        console.log("Creating new video call settings:", saveData);
        result = await dispatch(createVideoCallManagement(saveData)).unwrap();
        console.log("Create successful:", result);
      } else {
        // Update existing configuration
        console.log("Updating video call settings:", {
          id: latestSettings.id,
          data: saveData
        });
        result = await dispatch(updateVideoCallManagement({
          id: latestSettings.id,
          data: saveData
        })).unwrap();
        console.log("Update successful:", result);
      }

    } catch (error) {
      console.error("Failed to save video call settings:", error);
      // You could add a toast notification here
    } finally {
      setIsSaving(false);
    }
  };

  const getDurationLabel = (duration: VideoCallManagementCallDurationEnum) => {
    const labels = {
      [VideoCallManagementCallDurationEnum["30 mins"]]: "30 minutes",
      [VideoCallManagementCallDurationEnum["1 hour"]]: "1 hour",
      [VideoCallManagementCallDurationEnum["2 hours"]]: "2 hours",
      [VideoCallManagementCallDurationEnum.Unlimited]: "Unlimited"
    };
    return labels[duration] || "Unknown";
  };

  const getQualityLabel = (quality: VideoCallManagementVideoQualityEnum) => {
    const labels = {
      [VideoCallManagementVideoQualityEnum.Low]: "Low (480p)",
      [VideoCallManagementVideoQualityEnum.Medium]: "Medium (720p)",
      [VideoCallManagementVideoQualityEnum.High]: "High (1080p)",
      [VideoCallManagementVideoQualityEnum.Auto]: "Auto"
    };
    return labels[quality] || "Unknown";
  };

  const getRegionLabel = (region: VideoCallManagementServerRegionEnum) => {
    const labels = {
      [VideoCallManagementServerRegionEnum["North America"]]: "North America",
      [VideoCallManagementServerRegionEnum.Europe]: "Europe",
      [VideoCallManagementServerRegionEnum.Asia]: "Asia",
      [VideoCallManagementServerRegionEnum.Auto]: "Auto Select"
    };
    return labels[region] || "Unknown";
  };

  if (latestSettingsLoading && !latestSettings) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading video call settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ textAlign: "left" }}>
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="text-lg font-semibold">Video Call Management</h2>
          <p className="text-gray-600 mt-1">
            {latestSettings ? "Configure and monitor video call settings" : "Create and configure video call settings"}
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            onClick={handleSave}
            disabled={(!isFormDirty && !!latestSettings) || isSaving}
            className="flex items-center gap-2 flex-1 sm:flex-auto"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {latestSettings ? "Save Settings" : "Create Settings"}
          </Button>
        </div>
      </div>

      {latestSettingsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{latestSettingsError}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dispatch(clearLatestSettingsError())}
            className="mt-2 text-red-600"
          >
            Dismiss
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Active Calls</p>
              <p className="text-xl sm:text-2xl font-semibold">12</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <Video className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Bandwidth Usage</p>
              <p className="text-xl sm:text-2xl font-semibold">2.4 GB</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart2 className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Failed Calls</p>
              <p className="text-xl sm:text-2xl font-semibold">2</p>
            </div>
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">Avg Call Duration</p>
              <p className="text-xl sm:text-2xl font-semibold">15m</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Clock className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="font-medium mb-4">Video Call Settings</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center p-3 border rounded-lg">
            <div>
              <div className="font-medium">Enable Video Calls</div>
              <p className="text-sm text-gray-500">Allow video calls between professionals and customers</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={formState.basicSettings.enableVideoCall}
                onChange={(e) => handleToggle("enableVideoCall", e.target.checked)}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex justify-between items-center p-3 border rounded-lg">
            <div>
              <div className="font-medium">HD Quality</div>
              <p className="text-sm text-gray-500">Enable HD video quality when bandwidth permits</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={formState.basicSettings.enableHDQuality}
                onChange={(e) => handleToggle("enableHDQuality", e.target.checked)}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex justify-between items-center p-3 border rounded-lg">
            <div>
              <div className="font-medium">Call Recording</div>
              <p className="text-sm text-gray-500">Record video calls for quality and training purposes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={formState.basicSettings.enableCallRecording}
                onChange={(e) => handleToggle("enableCallRecording", e.target.checked)}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex justify-between items-center p-3 border rounded-lg">
            <div>
              <div className="font-medium">Bandwidth Optimization</div>
              <p className="text-sm text-gray-500">Automatically adjust quality based on network conditions</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={formState.basicSettings.enableBandwidthOptimization}
                onChange={(e) => handleToggle("enableBandwidthOptimization", e.target.checked)}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-medium mb-4">Advanced Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Maximum Call Duration</label>
            <select 
              className="mt-1 block w-full px-3 py-2 border rounded-lg"
              value={formState.callLimits.callDuration}
              onChange={(e) => handleSelectChange("callLimits", "callDuration", parseInt(e.target.value))}
            >
              <option value={VideoCallManagementCallDurationEnum["30 mins"]}>30 minutes</option>
              <option value={VideoCallManagementCallDurationEnum["1 hour"]}>1 hour</option>
              <option value={VideoCallManagementCallDurationEnum["2 hours"]}>2 hours</option>
              <option value={VideoCallManagementCallDurationEnum.Unlimited}>Unlimited</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Current: {getDurationLabel(formState.callLimits.callDuration)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Video Quality Preset</label>
            <select 
              className="mt-1 block w-full px-3 py-2 border rounded-lg"
              value={formState.qualitySettings.videoQuality}
              onChange={(e) => handleSelectChange("qualitySettings", "videoQuality", parseInt(e.target.value))}
            >
              <option value={VideoCallManagementVideoQualityEnum.Low}>Low (480p)</option>
              <option value={VideoCallManagementVideoQualityEnum.Medium}>Medium (720p)</option>
              <option value={VideoCallManagementVideoQualityEnum.High}>High (1080p)</option>
              <option value={VideoCallManagementVideoQualityEnum.Auto}>Auto</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Current: {getQualityLabel(formState.qualitySettings.videoQuality)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Server Region</label>
            <select 
              className="mt-1 block w-full px-3 py-2 border rounded-lg"
              value={formState.serverSettings.serverRegion}
              onChange={(e) => handleSelectChange("serverSettings", "serverRegion", parseInt(e.target.value))}
            >
              <option value={VideoCallManagementServerRegionEnum["North America"]}>North America</option>
              <option value={VideoCallManagementServerRegionEnum.Europe}>Europe</option>
              <option value={VideoCallManagementServerRegionEnum.Asia}>Asia</option>
              <option value={VideoCallManagementServerRegionEnum.Auto}>Auto Select</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Current: {getRegionLabel(formState.serverSettings.serverRegion)}
            </p>
          </div>
        </div>
      </Card>

      {isFormDirty && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
              <span className="text-yellow-700">You have unsaved changes</span>
            </div>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              size="sm"
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              {latestSettings ? "Save Now" : "Create Now"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCallManagement;
